import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { getColorFromEmail } from '@shared/helpers/user-avatar-color-generator';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-lookup-chips',
  templateUrl: './lookup-chips.component.html',
  styleUrls: ['./lookup-chips.component.scss']
})
export class LookupChipsComponent implements OnInit, OnChanges {

  @Input() cssClass: string = '';
  @Input() label: string = '';
  @Input() floatLabel: 'always' | 'auto' = 'auto';
  @Input() inputId: string = '';
  @Input() placeHolder: string = '';
  @Input() options: any[] = [];
  @Input() valueProperty: string = '';
  @Input() textProperty: string = '';
  @Input() searchProperties: string[] = [];
  @Input() selectedOptionsLoading: boolean = false;
  @Input() infoText: string = '';
  @Input() formSubmitted: boolean = false;
  @Input() required: boolean = false;
  @Input() requiredText: string = '';
  @Input() matErrorId: string = '';
  @Input() hint: string = '';
  @Input() showRequiredStatusInLabel: boolean = false;
  @Input() hideRequiredMarker: boolean = false;
  @Input() selectable: boolean = true;
  @Input() removable: boolean = true;
  @Input() avatarChipTheme: boolean = false;
  @Input() chipColor: string = 'primary';
  @Input() verticalChipList: boolean = false;

  _selectedOptions: any[] = [];
  get selectedOptions() {
    return this._selectedOptions;
  }
  @Input()
  set selectedOptions(value) {
    this._selectedOptions = value;
    this.formCtrl.setValue(value);
  }

  _disabled = false;
  get disabled(): boolean { return this._disabled; }
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    this._disabled ? this.autoCompleteControl.disable() : this.autoCompleteControl.enable();
  };

  @Output() valueChanged = new EventEmitter<any>();

  formCtrl = new FormControl();
  autoCompleteControl = new FormControl();
  filteredOptions?: Observable<any[]>;
  readonly VIRTUAL_SCROLL_THRESHOLD = 7;

  @ViewChild('chipInput') chipInput!: ElementRef;

  constructor(@Inject(DOCUMENT) private document: Document) { }

  ngOnInit() {
    this.setFilterdOptions();

    if (this.searchProperties.indexOf(this.textProperty) === -1) {
      this.searchProperties.push(this.textProperty);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {

      if (this.avatarChipTheme) {
        this.options.forEach(option => {
          option['userInitals'] = option['email'].charAt(0).toLocaleUpperCase();
          option['avatarBackgroundColor'] = getColorFromEmail(option['email']);
        });
      }

      this.setFilterdOptions();
    }
  }

  add(event: MatChipInputEvent): void {
    const input = event.chipInput;
    const value = event.value;

    if ((value || '').trim()) {
      const option = this.options.find(item => item[this.textProperty] === value);
      if (option && !this.selectedOptions.includes(option)) {
        this._selectedOptions = [...this._selectedOptions, option];
        this.formCtrl.setValue(this.selectedOptions);
        this.valueChanged.emit(this.selectedOptions);
        this.scrollToBottom();
      }
    }

    // Reset the input value
    if (input) {
      input.clear();
    }

    this.autoCompleteControl.setValue('');
  }

  remove(option: any): void {
    const index = this.selectedOptions.indexOf(option);

    if (index >= 0) {
      this._selectedOptions = this._selectedOptions.filter((option, i) => i !== index);
      this.formCtrl.setValue(this.selectedOptions);
      this.valueChanged.emit(this.selectedOptions);
    }
  }

  selected(option: any): void {
    if (!this.selectedOptions.includes(option)) {
      this._selectedOptions = [...this._selectedOptions, option];
      this.formCtrl.setValue(this.selectedOptions);
      this.valueChanged.emit(this.selectedOptions);
      this.chipInput.nativeElement.value = '';
      this.autoCompleteControl.setValue('');
      this.scrollToBottom();
    }
  }

  clear() {
    this.selectedOptions = [];
    this.formCtrl.setValue(this.selectedOptions);
    this.valueChanged.emit(this.selectedOptions);
  }

  onInputFocus() {
    this.scrollToBottom();
    this.chipInput.nativeElement.focus();
  }

  private setFilterdOptions() {
    this.filteredOptions = this.autoCompleteControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        map(value => typeof value === 'string' ? value : value && value[this.textProperty]),
        map(queryText => {
          // First filter out already selected options
          const unselectedOptions = this.options.filter(option =>
            !this.selectedOptions.some(selected => selected[this.valueProperty] === option[this.valueProperty])
          );

          // Then apply text search if there's a query
          return queryText
            ? unselectedOptions.filter(option =>
              this.searchProperties.some(property =>
                option[property]?.toLowerCase()?.includes(queryText.toLowerCase())
              )
            )
            : unselectedOptions;
        }),
        map(options => options.slice(0, 100))
      );
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const chipGridElement = document.getElementById(`${this.inputId}ChipGrid`);
      if (chipGridElement) {
        chipGridElement.scrollTop = chipGridElement.scrollHeight;
      }
    });
  }

}
